/**
 * @fileoverview State hook for the "My tasks" list: loads the (hydrated) tasks,
 * derives project filter options, syncs the selected project to the URL and
 * tracks optimistically-completed tasks. Defensive + anti-race (mountedRef).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ALL_PROJECTS } from '../constants'
import { dataSource } from '../dataSource'
import type { StudentTask } from '../types'
import type { ProjectOption } from '../components'

interface UseStudentTasksReturn {
  tasks: readonly StudentTask[]
  projectOptions: readonly ProjectOption[]
  loading: boolean
  error: string | null
  selectedProjectId: string
  setProjectId: (projectId: string) => void
  markDone: (taskId: string) => void
  refresh: () => void
}

const PARAM_PROJECT = 'project'

export function useStudentTasks(): UseStudentTasksReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allTasks, setAllTasks] = useState<readonly StudentTask[]>([])
  const [doneIds, setDoneIds] = useState<ReadonlySet<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    dataSource
      .fetchTasks()
      .then(spine => {
        if (cancelled || !mountedRef.current) return
        // Show the spine immediately (smooth), then hydrate each card in the
        // background and patch it in as its detail arrives.
        setAllTasks(spine)
        setDoneIds(new Set(spine.filter(task => task.done).map(task => task.id)))
        setLoading(false)
        spine.forEach(task => {
          dataSource
            .hydrateTask(task)
            .then(hydrated => {
              if (cancelled || !mountedRef.current) return
              setAllTasks(prev => prev.map(item => (item.id === hydrated.id ? hydrated : item)))
            })
            .catch(() => undefined)
        })
      })
      .catch(err => {
        if (cancelled || !mountedRef.current) return
        setError(err instanceof Error ? err.message : 'Failed to load tasks')
        setAllTasks([])
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [reloadToken])

  const selectedProjectId = searchParams.get(PARAM_PROJECT) ?? ALL_PROJECTS

  const withDone = useMemo<readonly StudentTask[]>(
    () => allTasks.map(task => (doneIds.has(task.id) ? { ...task, done: true } : task)),
    [allTasks, doneIds],
  )

  const projectOptions = useMemo<readonly ProjectOption[]>(() => {
    const seen = new Map<string, string>()
    withDone.forEach(task => {
      if (task.projectId && task.projectName && !seen.has(task.projectId)) {
        seen.set(task.projectId, task.projectName)
      }
    })
    return Array.from(seen, ([value, label]) => ({ value, label }))
  }, [withDone])

  const tasks = useMemo<readonly StudentTask[]>(
    () =>
      selectedProjectId === ALL_PROJECTS
        ? withDone
        : withDone.filter(task => task.projectId === selectedProjectId),
    [withDone, selectedProjectId],
  )

  const setProjectId = useCallback(
    (projectId: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          projectId && projectId !== ALL_PROJECTS
            ? next.set(PARAM_PROJECT, projectId)
            : next.delete(PARAM_PROJECT)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const markDone = useCallback(
    (taskId: string) => setDoneIds(prev => new Set(prev).add(taskId)),
    [],
  )

  const refresh = useCallback(() => setReloadToken(token => token + 1), [])

  return {
    tasks,
    projectOptions,
    loading,
    error,
    selectedProjectId,
    setProjectId,
    markDone,
    refresh,
  }
}
